import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import orderBy from 'lodash/orderBy';

import { AVERAGE_HOURS_IN_MONTH } from '@cast/constants';
import type {
  CostReport,
  DailyCostReportResponse,
  ClusterHourlyCost,
} from '@cast/types';
import { toNumOrZero } from '@cast/utils';

import type { SpendBreakdownChartPoint } from './dashboardCharts';

dayjs.extend(utc);

const DAYS_PER_MONTH = AVERAGE_HOURS_IN_MONTH / 24;
const HOURS_PER_DAY = 24;

type IsAllZeroArgs = {
  point: SpendBreakdownChartPoint;
};

const isAllZero = ({ point }: IsAllZeroArgs): boolean =>
  point.onDemand === 0 && point.spot === 0 && point.fallback === 0;

type TrimEdgeZeroSpendPointsArgs = {
  points: SpendBreakdownChartPoint[];
};

/** Drop leading/trailing all-zero points so range edges do not pin the series to zero. */
export const trimEdgeZeroSpendPoints = ({
  points,
}: TrimEdgeZeroSpendPointsArgs): SpendBreakdownChartPoint[] => {
  if (!points.length) {
    return points;
  }

  let start = 0;
  while (start < points.length && isAllZero({ point: points[start] })) {
    start += 1;
  }

  let end = points.length - 1;
  while (end >= start && isAllZero({ point: points[end] })) {
    end -= 1;
  }

  if (start > end) {
    return [];
  }

  return points.slice(start, end + 1);
};

type ComputeMonthlyProjectionArgs = {
  chartData: SpendBreakdownChartPoint[];
  pick: (point: SpendBreakdownChartPoint) => number;
};

const computeMonthlyProjection = ({
  chartData,
  pick,
}: ComputeMonthlyProjectionArgs): number => {
  if (!chartData.length) {
    return 0;
  }

  const total = chartData.reduce((sum, point) => sum + pick(point), 0);
  return (total / chartData.length) * DAYS_PER_MONTH;
};

type ToSpendBreakdownResultArgs = {
  chartData: SpendBreakdownChartPoint[];
};

const toSpendBreakdownResult = ({ chartData }: ToSpendBreakdownResultArgs) => {
  const trimmed = trimEdgeZeroSpendPoints({ points: chartData });

  return {
    chartData: trimmed,
    // KPIs average the full daily series so zero-cost boundary days remain valid.
    currentSpendMonthly: computeMonthlyProjection({
      chartData,
      pick: (p) => p.total,
    }),
    onDemandSpendMonthly: computeMonthlyProjection({
      chartData,
      pick: (p) => p.onDemand,
    }),
    spotSpendMonthly: computeMonthlyProjection({
      chartData,
      pick: (p) => p.spot,
    }),
    fallbackSpendMonthly: computeMonthlyProjection({
      chartData,
      pick: (p) => p.fallback,
    }),
  };
};

type ToChartPointArgs = {
  timestamp: string;
  onDemand: number;
  spot: number;
  fallback: number;
};

const toChartPoint = ({
  timestamp,
  onDemand,
  spot,
  fallback,
}: ToChartPointArgs): SpendBreakdownChartPoint => {
  const day = dayjs.utc(timestamp).startOf('day');
  return {
    timestamp: day.toISOString(),
    chartTimestampUnix: day.unix(),
    onDemand,
    spot,
    fallback,
    total: onDemand + spot + fallback,
  };
};

type MapClusterCostReportToSpendBreakdownArgs = {
  items?: CostReport[];
};

/**
 * Maps cluster cost-report interval items (period costs for the step) to daily
 * spend breakdown chart data + monthly KPI projections.
 */
export const mapClusterCostReportToSpendBreakdown = ({
  items = [],
}: MapClusterCostReportToSpendBreakdownArgs = {}) => {
  const chartData = orderBy(
    items.filter((item) => !!item.timestamp),
    'timestamp'
  ).map((item) =>
    toChartPoint({
      timestamp: item.timestamp,
      onDemand: toNumOrZero(item.costOnDemand),
      spot: toNumOrZero(item.costSpot),
      fallback: toNumOrZero(item.costSpotFallback),
    })
  );

  return toSpendBreakdownResult({ chartData });
};

type AggregatedHourly = {
  timestamp: string;
  onDemandHourly: number;
  spotHourly: number;
  fallbackHourly: number;
};

type AggregateOrgIntervalsByDayArgs = {
  intervals: ClusterHourlyCost[];
};

const aggregateOrgIntervalsByDay = ({
  intervals,
}: AggregateOrgIntervalsByDayArgs): AggregatedHourly[] => {
  const byDay = new Map<string, AggregatedHourly>();

  for (const interval of intervals) {
    if (!interval.timestamp) {
      continue;
    }

    const dayKey = dayjs.utc(interval.timestamp).startOf('day').toISOString();
    const existing = byDay.get(dayKey);

    const onDemandHourly = toNumOrZero(interval.costOnDemandPerHour);
    const spotHourly = toNumOrZero(interval.costSpotPerHour);
    const fallbackHourly = toNumOrZero(interval.costSpotFallbackPerHour);

    if (existing) {
      existing.onDemandHourly += onDemandHourly;
      existing.spotHourly += spotHourly;
      existing.fallbackHourly += fallbackHourly;
    } else {
      byDay.set(dayKey, {
        timestamp: dayKey,
        onDemandHourly,
        spotHourly,
        fallbackHourly,
      });
    }
  }

  return orderBy([...byDay.values()], 'timestamp');
};

type MapClustersDailyCostsToSpendBreakdownArgs = {
  report?: DailyCostReportResponse;
};

/**
 * Aggregates org daily-cost intervals across clusters (hourly rates) into daily
 * spend breakdown chart data + monthly KPI projections.
 */
export const mapClustersDailyCostsToSpendBreakdown = ({
  report,
}: MapClustersDailyCostsToSpendBreakdownArgs = {}) => {
  const intervals: ClusterHourlyCost[] = [];
  for (const cluster of report?.items ?? []) {
    if (cluster.intervals?.length) {
      intervals.push(...cluster.intervals);
    }
  }

  const chartData = aggregateOrgIntervalsByDay({ intervals }).map((day) =>
    toChartPoint({
      timestamp: day.timestamp,
      onDemand: day.onDemandHourly * HOURS_PER_DAY,
      spot: day.spotHourly * HOURS_PER_DAY,
      fallback: day.fallbackHourly * HOURS_PER_DAY,
    })
  );

  return toSpendBreakdownResult({ chartData });
};
